import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  viewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import { getRealOptionLabel, IOption } from '../input.types';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { InputFieldSelectionBaseComponent } from './input-field-selection-base.component';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { AsyncPipe } from '@angular/common';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isDifferent } from '@mng/mng-ts-helpers';

export type OptionComparator = (option: IOption, value: any) => boolean;

@Component({
  selector: 'mng-inputfield-autocomplete',
  imports: [
    MatFormField,
    MatSuffix,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    ExtendedModule,
    MatIconButton,
    MatTooltip,
    MatIcon,
    AsyncPipe,
    MatError
  ],
  template: `
    <mat-form-field
      style="width: 100%"
      [ngStyle]="ngStyle()"
      [appearance]="appearance()"
      [subscriptSizing]="subscriptSizing()"
      [floatLabel]="floatLabel()"
    >
      @if (field().label) {
        <mat-label>{{ field().label }}</mat-label>
      }

      @if (hasErrors) {
        <mat-error> {{ validationMessage }} </mat-error>
      }

      @if (withFilterDeleteButton()) {
        <div>
          <input
            #autocompleteInput
            #trigger="matAutocompleteTrigger"
            matInput
            [style]="inputTextStyle()"
            [id]="field().id"
            [formControl]="field().control"
            [matAutocomplete]="auto"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayFn()"
            [panelWidth]="panelWidth()"
            (optionSelected)="onOptionSelected($event)"
            (closed)="onAutocompleteClosed()"
          >
            @for (option of filteredOptions | async; track trackBy($index, option)) {
              <mat-option
                [value]="option.value"
                [class.small-option-text]="smallText()"
                [innerHTML]="getRealOptionLabel(option)"
              />
            }
          </mat-autocomplete>
        </div>
      } @else {
        <input
          #autocompleteInput
          #trigger="matAutocompleteTrigger"
          matInput
          [style]="inputTextStyle()"
          [id]="field().id"
          [formControl]="field().control"
          [matAutocomplete]="auto"
        />
        <mat-autocomplete
          #auto="matAutocomplete"
          [panelWidth]="panelWidth()"
          (optionSelected)="onOptionSelected($event)"
          (closed)="onAutocompleteClosed()"
        >
          @for (option of filteredOptions | async; track trackBy($index, option)) {
            <mat-option
              [value]="option.value"
              [class.small-option-text]="smallText()"
              [innerHTML]="getRealOptionLabel(option)"
            />
          }
        </mat-autocomplete>
      }

      @if (field().control.value && withFilterDeleteButton() && !field().control.disabled) {
        <button
          mat-icon-button
          matIconSuffix
          matTooltip="Filter löschen"
          [disabled]="field().control.disabled"
          (click)="clearInput($event)"
        >
          <mat-icon svgIcon="cancel" style="color: gray; opacity: 0.5;" />
        </button>
      }
    </mat-form-field>
  `,
  styles: `
    .small-option-text {
      font-size: small;
      min-height: 28px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputFieldAutocompleteComponent extends InputFieldSelectionBaseComponent implements OnInit {
  readonly withFilterDeleteButton = input(true);
  readonly onlyOptionValuesAllowed = input(true);
  readonly panelWidth = input('');
  readonly autoCompleteTrigger = viewChild<MatAutocompleteTrigger>('trigger');
  readonly destroyRef = inject(DestroyRef);
  readonly selectedEvent = output<any>();
  lastSubmittedValue: any;

  filteredOptions: Observable<IOption[]>;

  readonly optionComparator = input<OptionComparator>((option: IOption, value: any) => option.value === value);

  readonly displayFn = input((value: any): string => {
    if (value) {
      const selectedOption: IOption | undefined = this.options().find((option) =>
        this.optionComparator()(option, value)
      );
      if (!selectedOption) {
        console.error(`Option für den value ${value} wurde nicht gefunden`);
      } else {
        return getRealOptionLabel(selectedOption);
      }
    }
    return '';
  });

  ngOnInit() {
    this.filteredOptions = this.field().control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      startWith(''),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return name ? this._filter(name as string) : this.options().slice();
      })
    );
  }

  private _filter(name: string): IOption[] {
    const filterValue = name.toLowerCase();
    return this.options().filter((option) => getRealOptionLabel(option).toLowerCase().includes(filterValue));
  }

  onOptionSelected(selected: MatAutocompleteSelectedEvent) {
    const value = selected.option?.value ?? undefined;
    this.emitValue(value);
  }

  clearInput(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.field().control.setValue(undefined);
    this.field().control.markAsDirty();
    this.emitValue(undefined);
  }

  onAutocompleteClosed() {
    // wenn im input feld ein wert eingegeben wurde der nicht im options array
    // wird der control value auf "undefined" gesetzt

    if (this.onlyOptionValuesAllowed() && !this.field().isOptionValueSelected()) {
      this.field().control.setValue(undefined);
      this.emitValue(undefined);
    } else {
      // wenn z.B. ein gültiger Wert "manuell" eingegeben wurde,
      // aber nicht mit Enter oder Maus ausgewählt wurde sondern mit Tab
      // dann wird "optionSelected" nicht ausgelöst.
      // um auch in dieser Konstellation den event zu emitten wird das hier
      // abgefangen
      if (isDifferent(this.lastSubmittedValue, this.field().control.value)) {
        this.emitValue(this.field().control.value);
        // der wert wird "explizit" nochmals gesetzt damit die Anzeige richtig gestellt wird
        // (z.B. könnte ansonsten nur den eingegeben Wert angezeigt werden und nicht das was durch die
        // displayFn angezeigt wird)
        //
        this.field().control.setValue(this.field().control.value);
      }
    }
  }

  emitValue(value: any) {
    this.selectedEvent.emit(value);
    this.lastSubmittedValue = value;
  }

  openPanel() {
    this.autoCompleteTrigger().openPanel();
  }
}
